/**
 * usePricingExport
 *
 * Hook for exporting pricing calculator summary to PDF and Excel formats.
 * Creates professional, branded documents inspired by DealHub and Stripe quotes.
 *
 * @see src/modules/RetoolModule/components/PricingCalculatorSummary.tsx
 */

import { useCallback } from 'react';
import jsPDF from 'jspdf';
import ExcelJS, { type Border, type Fill, type Style } from 'exceljs';
import { saveAs } from 'file-saver';
import {
  PricingCalculatorFormValues,
  PricingCalculatorSummary,
  TenantType,
  isDedicatedTenant,
  getPlanTypeLabel,
} from '../types/pricingCalculator';

// Brand colors for PDF styling
const COLORS = {
  primary: { r: 30, g: 58, b: 95 }, // Dark navy blue
  accent: { r: 241, g: 90, b: 79 }, // Coral/red accent from Mosaic logo
  success: { r: 133, g: 205, b: 148 }, // Green from logo
  text: { r: 51, g: 51, b: 51 },
  textLight: { r: 107, g: 114, b: 128 },
  border: { r: 229, g: 231, b: 235 },
  background: { r: 249, g: 250, b: 251 },
  white: { r: 255, g: 255, b: 255 },
};

// Mosaic icon SVG - the colorful mosaic pattern (small, efficient)
const MOSAIC_ICON_SVG = `<svg width="64" height="64" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M11.1543 25.2961C12.8041 26.946 15.4791 26.946 17.1289 25.2961L18.8556 23.5695L14.1416 18.8555L9.42761 23.5695L11.1543 25.2961Z" fill="#F15A4F"/>
<rect width="6.66659" height="6.66659" transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 18.8555 23.5702)" fill="#C25ECB"/>
<path d="M23.5693 18.8562L25.296 17.1295C26.9459 15.4797 26.9459 12.8047 25.296 11.1549L23.5693 9.42822L18.8553 14.1422L23.5693 18.8562Z" fill="#067BBC"/>
<rect width="6.66659" height="6.66659" transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 18.8555 14.1425)" fill="#1DA4A2"/>
<rect width="6.66659" height="6.66659" transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 14.1416 18.8563)" fill="#989F8F"/>
<rect width="6.66659" height="6.66659" transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 9.42773 23.5702)" fill="#E98A1C"/>
<path d="M14.1416 9.42798L18.8556 4.71399L17.1289 2.98733C15.4791 1.33747 12.8041 1.33747 11.1543 2.98733L9.42761 4.71399L14.1416 9.42798Z" fill="#85CD94"/>
<rect width="6.66659" height="6.66659" transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 9.42773 14.1423)" fill="#ACCA56"/>
<path d="M4.71387 18.8562L9.42786 14.1422L4.71387 9.42822L2.98721 11.1549C1.33735 12.8047 1.33735 15.4797 2.98721 17.1295L4.71387 18.8562Z" fill="#E0BF53"/>
</svg>`;

// Convert SVG to PNG data URL using canvas (small, efficient for PDF)
const svgToPngDataUrl = (svgString: string, size: number): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    const img = new Image();
    const svgBase64 = btoa(unescape(encodeURIComponent(svgString)));
    const dataUrl = `data:image/svg+xml;base64,${svgBase64}`;

    img.onload = () => {
      ctx.drawImage(img, 0, 0, size, size);
      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = () => reject(new Error('Failed to load SVG'));
    img.src = dataUrl;
  });
};

interface UsePricingExportProps {
  values: PricingCalculatorFormValues;
  summary: PricingCalculatorSummary | null;
}

const formatCurrency = (value: number) => {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = () => {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const generateQuoteNumber = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const random = Math.floor(Math.random() * 10000)
    .toString()
    .padStart(4, '0');
  return `Q-${year}${month}-${random}`;
};

type PdfLayout = {
  pageWidth: number;
  pageHeight: number;
  margin: number;
  contentWidth: number;
};

/** Mutable vertical cursor threaded through the drawing helpers, mirroring the
 *  single `yPos` local the PDF builder used before it was split by section. */
type PdfCursor = { y: number };

const addTableRow = (
  doc: jsPDF,
  { pageWidth, pageHeight, margin }: PdfLayout,
  cursor: PdfCursor,
  item: string,
  qty: string,
  amount: string,
  isSubItem = false,
  isBold = false,
) => {
  if (cursor.y > pageHeight - 50) {
    doc.addPage();
    // Add accent bar on new page
    doc.setFillColor(COLORS.primary.r, COLORS.primary.g, COLORS.primary.b);
    doc.rect(0, 0, pageWidth, 4, 'F');
    cursor.y = 20;
  }

  doc.setFontSize(10);
  doc.setFont('helvetica', isBold ? 'bold' : 'normal');
  doc.setTextColor(COLORS.text.r, COLORS.text.g, COLORS.text.b);

  const xOffset = isSubItem ? margin + 10 : margin + 5;
  if (isSubItem) {
    doc.setTextColor(COLORS.textLight.r, COLORS.textLight.g, COLORS.textLight.b);
  }

  doc.text(item, xOffset, cursor.y);
  if (qty) doc.text(qty, pageWidth - margin - 70, cursor.y, { align: 'right' });
  doc.text(amount, pageWidth - margin - 5, cursor.y, { align: 'right' });

  cursor.y += 8;
};

const addSectionHeader = (
  doc: jsPDF,
  { pageWidth, pageHeight, margin, contentWidth }: PdfLayout,
  cursor: PdfCursor,
  title: string,
) => {
  if (cursor.y > pageHeight - 60) {
    doc.addPage();
    doc.setFillColor(COLORS.primary.r, COLORS.primary.g, COLORS.primary.b);
    doc.rect(0, 0, pageWidth, 4, 'F');
    cursor.y = 20;
  }

  cursor.y += 4;
  doc.setFillColor(COLORS.primary.r, COLORS.primary.g, COLORS.primary.b);
  doc.rect(margin, cursor.y - 4, contentWidth, 8, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLORS.white.r, COLORS.white.g, COLORS.white.b);
  doc.text(title, margin + 5, cursor.y + 1);
  cursor.y += 10;
};

// ===== HEADER SECTION =====
const drawQuoteHeader = async (
  doc: jsPDF,
  { pageWidth, margin }: PdfLayout,
  quoteNumber: string,
) => {
  // Top accent bar
  doc.setFillColor(COLORS.primary.r, COLORS.primary.g, COLORS.primary.b);
  doc.rect(0, 0, pageWidth, 4, 'F');

  let yPos = 20;

  // Logo - convert SVG icon to PNG (small, efficient) + text
  try {
    const iconPng = await svgToPngDataUrl(MOSAIC_ICON_SVG, 64);
    // Icon is square, display at 12x12mm
    doc.addImage(iconPng, 'PNG', margin, yPos - 2, 12, 12);
    // Add "Mosaic" text next to icon
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLORS.primary.r, COLORS.primary.g, COLORS.primary.b);
    doc.text('Mosaic', margin + 14, yPos + 7);
  } catch {
    // Fallback: draw text logo if image conversion fails
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLORS.primary.r, COLORS.primary.g, COLORS.primary.b);
    doc.text('Mosaic', margin, yPos + 10);
  }

  // Quote title and number on right
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLORS.primary.r, COLORS.primary.g, COLORS.primary.b);
  doc.text('Quote', pageWidth - margin, yPos + 5, { align: 'right' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(COLORS.textLight.r, COLORS.textLight.g, COLORS.textLight.b);
  doc.text(quoteNumber, pageWidth - margin, yPos + 12, { align: 'right' });
  doc.text(formatDate(), pageWidth - margin, yPos + 18, { align: 'right' });

  // Horizontal divider
  yPos = 48;
  doc.setDrawColor(COLORS.border.r, COLORS.border.g, COLORS.border.b);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
};

// ===== CUSTOMER INFO SECTION =====
const drawCustomerInfo = (
  doc: jsPDF,
  { pageWidth, margin }: PdfLayout,
  values: PricingCalculatorFormValues,
) => {
  const yPos = 58;

  // Two-column layout for customer info
  const leftColX = margin;
  const rightColX = pageWidth / 2 + 10;

  // Left column - Prepared For
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLORS.textLight.r, COLORS.textLight.g, COLORS.textLight.b);
  doc.text('PREPARED FOR', leftColX, yPos);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLORS.text.r, COLORS.text.g, COLORS.text.b);
  doc.text(values.companyName || '—', leftColX, yPos + 8);

  // Right column - Quote Details
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLORS.textLight.r, COLORS.textLight.g, COLORS.textLight.b);
  doc.text('QUOTE DETAILS', rightColX, yPos);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(COLORS.text.r, COLORS.text.g, COLORS.text.b);

  const detailsY = yPos + 8;
  doc.text(`Duration: ${values.subscriptionDuration} months`, rightColX, detailsY);
  doc.text(`Type: ${values.isRenewal ? 'Renewal' : 'New Subscription'}`, rightColX, detailsY + 6);
  doc.text(`Valid for: 30 days`, rightColX, detailsY + 12);
};

// ===== LINE ITEMS TABLE =====
const drawLineItemsTableHeader = (
  doc: jsPDF,
  { pageWidth, margin, contentWidth }: PdfLayout,
  cursor: PdfCursor,
) => {
  // Table header
  doc.setFillColor(COLORS.background.r, COLORS.background.g, COLORS.background.b);
  doc.rect(margin, cursor.y, contentWidth, 10, 'F');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLORS.textLight.r, COLORS.textLight.g, COLORS.textLight.b);
  doc.text('ITEM', margin + 5, cursor.y + 7);
  doc.text('QTY', pageWidth - margin - 70, cursor.y + 7, { align: 'right' });
  doc.text('AMOUNT', pageWidth - margin - 5, cursor.y + 7, { align: 'right' });

  cursor.y += 15;
};

const drawMemberAndPlatformRows = (
  doc: jsPDF,
  layout: PdfLayout,
  cursor: PdfCursor,
  values: PricingCalculatorFormValues,
  summary: PricingCalculatorSummary,
) => {
  // Member Pricing
  addTableRow(
    doc,
    layout,
    cursor,
    'Member License',
    `${values.numberOfMembers} users`,
    formatCurrency(summary.totalMemberCostYearly),
    false,
    true,
  );
  addTableRow(
    doc,
    layout,
    cursor,
    `${formatCurrency(summary.discountedPricePerMember)}/user/month`,
    '',
    '',
    true,
  );

  // Platform Fee
  if (summary.platformFeeYearly > 0) {
    addTableRow(
      doc,
      layout,
      cursor,
      'Platform Fee',
      `${values.subscriptionDuration} months`,
      formatCurrency(summary.platformFeeYearly),
    );
    addTableRow(
      doc,
      layout,
      cursor,
      `${formatCurrency(summary.platformFeeMonthly)}/month • ${summary.hoursIncluded} hrs included`,
      '',
      '',
      true,
    );
  }
};

const drawAdditionalUserRows = (
  doc: jsPDF,
  layout: PdfLayout,
  cursor: PdfCursor,
  values: PricingCalculatorFormValues,
  summary: PricingCalculatorSummary,
) => {
  // Additional Users
  if (
    values.numberOfInternalContractors > 0 ||
    values.numberOfExternalContractors > 0 ||
    values.numberOfGuests > 0
  ) {
    cursor.y += 2;
    if (values.numberOfInternalContractors > 0) {
      addTableRow(
        doc,
        layout,
        cursor,
        'Internal Contractors',
        `${values.numberOfInternalContractors} users`,
        formatCurrency(summary.internalContractorsTotal),
      );
    }
    if (values.numberOfExternalContractors > 0) {
      addTableRow(
        doc,
        layout,
        cursor,
        'External Contractors',
        `${values.numberOfExternalContractors} users`,
        formatCurrency(summary.externalContractorsTotal),
      );
    }
    if (values.numberOfGuests > 0) {
      addTableRow(
        doc,
        layout,
        cursor,
        'Guests',
        `${values.numberOfGuests} users`,
        formatCurrency(summary.guestsTotal),
      );
    }
  }
};

const drawServerUpgradeRows = (
  doc: jsPDF,
  layout: PdfLayout,
  cursor: PdfCursor,
  values: PricingCalculatorFormValues,
  summary: PricingCalculatorSummary,
) => {
  // Server Upgrades
  if (
    summary.isolatedDatabase ||
    isDedicatedTenant(summary.tenantType) ||
    summary.securityPackageSso ||
    summary.govCloud
  ) {
    cursor.y += 2;
    if (summary.isolatedDatabase) {
      addTableRow(
        doc,
        layout,
        cursor,
        'Isolated Database',
        '',
        formatCurrency(summary.isolatedDatabaseFeeYearly),
      );
    }
    if (isDedicatedTenant(summary.tenantType) && summary.tenantFee > 0) {
      const tenantLabel =
        summary.tenantType === TenantType.SINGLE
          ? 'Single Tenant Environment'
          : 'Enterprise Tenant';
      addTableRow(doc, layout, cursor, tenantLabel, '', formatCurrency(summary.tenantFee));
    }
    if (summary.securityPackageSso) {
      addTableRow(
        doc,
        layout,
        cursor,
        'SSO Security Package',
        `${values.numberOfMembers} users`,
        formatCurrency(summary.ssoTotalYearly),
      );
    }
    if (summary.govCloud) {
      addTableRow(doc, layout, cursor, 'Gov Cloud', '', 'Included');
    }
  }
};

const drawAddOnRows = (
  doc: jsPDF,
  layout: PdfLayout,
  cursor: PdfCursor,
  summary: PricingCalculatorSummary,
) => {
  // Add-ons
  if (
    summary.aiForecasting ||
    summary.nonPeopleResources ||
    summary.profileBuilding ||
    summary.skillsTagging ||
    summary.solutionsSupport ||
    summary.enableWhiteLabel
  ) {
    cursor.y += 2;
    if (summary.aiForecasting) {
      addTableRow(
        doc,
        layout,
        cursor,
        'AI Forecasting',
        '',
        formatCurrency(summary.aiForecastingFeeAfterDiscount),
      );
    }
    if (summary.nonPeopleResources) {
      addTableRow(
        doc,
        layout,
        cursor,
        'Non-People Resources',
        '',
        formatCurrency(summary.nonPeopleResourcesFeeAfterDiscount),
      );
    }
    if (summary.profileBuilding) {
      addTableRow(
        doc,
        layout,
        cursor,
        'Profile Building',
        '',
        formatCurrency(summary.profileBuildingFeeAfterDiscount),
      );
    }
    if (summary.skillsTagging) {
      addTableRow(
        doc,
        layout,
        cursor,
        'Skills Tagging',
        '',
        formatCurrency(summary.skillsTaggingFeeAfterDiscount),
      );
    }
    if (summary.solutionsSupport) {
      addTableRow(
        doc,
        layout,
        cursor,
        'Solutions Support',
        '',
        formatCurrency(summary.solutionsSupportFee),
      );
    }
    if (summary.enableWhiteLabel) {
      addTableRow(
        doc,
        layout,
        cursor,
        'White-Label Branding',
        '',
        formatCurrency(summary.whiteLabelAdditionalCost),
      );
    }
  }
};

/** Divider + bold subtotal row, shared by the recurring and one-time sections. */
const drawSubtotalRow = (
  doc: jsPDF,
  layout: PdfLayout,
  cursor: PdfCursor,
  label: string,
  amount: string,
) => {
  const { pageWidth, margin } = layout;

  cursor.y += 4;
  doc.setDrawColor(COLORS.border.r, COLORS.border.g, COLORS.border.b);
  doc.line(margin, cursor.y, pageWidth - margin, cursor.y);
  cursor.y += 8;
  addTableRow(doc, layout, cursor, label, '', amount, false, true);
};

const drawOneTimeFeeRows = (
  doc: jsPDF,
  layout: PdfLayout,
  cursor: PdfCursor,
  values: PricingCalculatorFormValues,
  summary: PricingCalculatorSummary,
) => {
  // Onboarding
  addTableRow(
    doc,
    layout,
    cursor,
    'Onboarding Fee',
    '',
    formatCurrency(summary.onboardingFeeAfterDiscount),
  );
  if (values.discountOnOnboardingFee > 0) {
    addTableRow(
      doc,
      layout,
      cursor,
      `${values.discountOnOnboardingFee}% discount applied`,
      '',
      `(${formatCurrency(summary.onboardingFee - summary.onboardingFeeAfterDiscount)} saved)`,
      true,
    );
  }

  // Direct Database (one-time)
  if (summary.directDatabase) {
    addTableRow(
      doc,
      layout,
      cursor,
      'Direct Database Integration',
      '',
      formatCurrency(summary.directDatabaseFee),
    );
  }
};

// ===== TOTAL SECTION =====
const drawTotalSection = (
  doc: jsPDF,
  { pageWidth, margin, contentWidth }: PdfLayout,
  cursor: PdfCursor,
  summary: PricingCalculatorSummary,
) => {
  cursor.y += 10;
  doc.setFillColor(COLORS.primary.r, COLORS.primary.g, COLORS.primary.b);
  doc.roundedRect(margin, cursor.y, contentWidth, 20, 3, 3, 'F');

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLORS.white.r, COLORS.white.g, COLORS.white.b);
  doc.text('TOTAL', margin + 10, cursor.y + 13);
  doc.setFontSize(16);
  doc.text(formatCurrency(summary.totalCost), pageWidth - margin - 10, cursor.y + 13, {
    align: 'right',
  });
};

// ===== FOOTER =====
const drawQuoteFooter = (doc: jsPDF, { pageWidth, pageHeight, margin }: PdfLayout) => {
  const footerY = pageHeight - 25;

  // Footer divider
  doc.setDrawColor(COLORS.border.r, COLORS.border.g, COLORS.border.b);
  doc.line(margin, footerY - 10, pageWidth - margin, footerY - 10);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(COLORS.textLight.r, COLORS.textLight.g, COLORS.textLight.b);
  doc.text('This quote is valid for 30 days from the date of issue.', margin, footerY);
  doc.text(
    'For questions, please contact your Mosaic account representative.',
    margin,
    footerY + 5,
  );

  // Page number
  doc.text(`Page 1 of 1`, pageWidth - margin, footerY + 2, { align: 'right' });

  // Bottom accent bar
  doc.setFillColor(COLORS.primary.r, COLORS.primary.g, COLORS.primary.b);
  doc.rect(0, pageHeight - 4, pageWidth, 4, 'F');
};

export const usePricingExport = ({ values, summary }: UsePricingExportProps) => {
  // Export to PDF - DealHub/Stripe inspired design
  const exportToPDF = useCallback(async () => {
    if (!summary) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const layout: PdfLayout = {
      pageWidth,
      pageHeight,
      margin,
      contentWidth: pageWidth - margin * 2,
    };

    const quoteNumber = generateQuoteNumber();

    await drawQuoteHeader(doc, layout, quoteNumber);
    drawCustomerInfo(doc, layout, values);

    const cursor: PdfCursor = { y: 100 };
    drawLineItemsTableHeader(doc, layout, cursor);

    // === RECURRING FEES ===
    addSectionHeader(doc, layout, cursor, 'RECURRING FEES');
    drawMemberAndPlatformRows(doc, layout, cursor, values, summary);
    drawAdditionalUserRows(doc, layout, cursor, values, summary);
    drawServerUpgradeRows(doc, layout, cursor, values, summary);
    drawAddOnRows(doc, layout, cursor, summary);
    drawSubtotalRow(
      doc,
      layout,
      cursor,
      'Recurring Subtotal',
      formatCurrency(summary.recurringFeeSubtotal),
    );

    // === ONE-TIME FEES ===
    cursor.y += 4;
    addSectionHeader(doc, layout, cursor, 'ONE-TIME FEES');
    drawOneTimeFeeRows(doc, layout, cursor, values, summary);
    drawSubtotalRow(
      doc,
      layout,
      cursor,
      'One-Time Subtotal',
      formatCurrency(summary.oneTimeFeeSubtotal),
    );

    drawTotalSection(doc, layout, cursor, summary);
    drawQuoteFooter(doc, layout);

    // Save the PDF
    const fileName = values.companyName
      ? `Mosaic_Quote_${values.companyName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`
      : `Mosaic_Quote_${new Date().toISOString().split('T')[0]}`;

    doc.save(`${fileName}.pdf`);
  }, [values, summary]);

  // Export to Excel - Professional table format with styling like DealHub/Stripe
  const exportToExcel = useCallback(async () => {
    if (!summary) return;

    const workbook = new ExcelJS.Workbook();
    const quoteNumber = generateQuoteNumber();

    // Style definitions (colors are ARGB)
    const solidFill = (argb: string): Fill => ({
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb },
    });
    const thinBorder = (argb: string): Partial<Border> => ({ style: 'thin', color: { argb } });

    const styles = {
      title: {
        font: { bold: true, size: 24, color: { argb: 'FF1E3A5F' } },
        alignment: { horizontal: 'left', vertical: 'middle' },
      },
      subtitle: {
        font: { bold: true, size: 16, color: { argb: 'FF1E3A5F' } },
        alignment: { horizontal: 'left', vertical: 'middle' },
      },
      label: {
        font: { bold: true, size: 11, color: { argb: 'FF6B7280' } },
        alignment: { horizontal: 'left', vertical: 'middle' },
      },
      value: {
        font: { size: 11, color: { argb: 'FF333333' } },
        alignment: { horizontal: 'left', vertical: 'middle' },
      },
      tableHeader: {
        font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' } },
        fill: solidFill('FF1E3A5F'),
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: thinBorder('FF1E3A5F'),
          bottom: thinBorder('FF1E3A5F'),
          left: thinBorder('FF1E3A5F'),
          right: thinBorder('FF1E3A5F'),
        },
      },
      sectionHeader: {
        font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' } },
        fill: solidFill('FF3B5998'),
        alignment: { horizontal: 'left', vertical: 'middle' },
      },
      itemRow: {
        font: { size: 10, color: { argb: 'FF333333' } },
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          bottom: thinBorder('FFE5E7EB'),
        },
      },
      itemRowAlt: {
        font: { size: 10, color: { argb: 'FF333333' } },
        fill: solidFill('FFF9FAFB'),
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          bottom: thinBorder('FFE5E7EB'),
        },
      },
      subtotal: {
        font: { bold: true, size: 11, color: { argb: 'FF1E3A5F' } },
        fill: solidFill('FFE5E7EB'),
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          top: thinBorder('FFD1D5DB'),
          bottom: thinBorder('FFD1D5DB'),
        },
      },
      total: {
        font: { bold: true, size: 14, color: { argb: 'FFFFFFFF' } },
        fill: solidFill('FF1E3A5F'),
        alignment: { horizontal: 'left', vertical: 'middle' },
      },
      currency: {
        numFmt: '"$"#,##0.00',
        alignment: { horizontal: 'right', vertical: 'middle' },
      },
      footer: {
        font: { size: 9, color: { argb: 'FF6B7280' }, italic: true },
        alignment: { horizontal: 'left', vertical: 'middle' },
      },
    } satisfies Record<string, Partial<Style>>;

    // Build styled data - each cell can have { v: value, s: style }
    type StyledCell = { v: string | number; s: Partial<Style> };
    type CellValue = string | StyledCell;
    const data: CellValue[][] = [];

    // Helper to create styled cell
    const cell = (value: string | number, style: Partial<Style>): StyledCell => ({
      v: value,
      s: style,
    });

    // Row counter for alternating colors
    let rowIndex = 0;

    // Header section
    data.push([cell('MOSAIC', styles.title), '', '']);
    data.push(['', '', '']);
    data.push([cell('PRICING QUOTE', styles.subtitle), '', '']);
    data.push(['', '', '']);
    data.push([cell('Quote Number:', styles.label), cell(quoteNumber, styles.value), '']);
    data.push([cell('Date:', styles.label), cell(formatDate(), styles.value), '']);
    data.push([
      cell('Valid Until:', styles.label),
      cell(
        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
        styles.value,
      ),
      '',
    ]);
    data.push(['', '', '']);
    data.push([cell('PREPARED FOR', styles.label), '', '']);
    data.push([cell('Company:', styles.label), cell(values.companyName || '—', styles.value), '']);
    data.push([
      cell('Duration:', styles.label),
      cell(`${values.subscriptionDuration} months`, styles.value),
      '',
    ]);
    data.push([
      cell('Type:', styles.label),
      cell(values.isRenewal ? 'Renewal' : 'New Subscription', styles.value),
      '',
    ]);
    data.push(['', '', '']);

    // Table header row
    data.push([
      cell('ITEM', styles.tableHeader),
      cell('QUANTITY', styles.tableHeader),
      cell('AMOUNT', styles.tableHeader),
    ]);

    // Section: RECURRING FEES
    data.push([
      cell('RECURRING FEES', styles.sectionHeader),
      cell('', styles.sectionHeader),
      cell('', styles.sectionHeader),
    ]);

    // Helper to add item row with alternating colors
    const addItemRow = (item: string, qty: string, amount: number | string) => {
      const rowStyle = rowIndex % 2 === 0 ? styles.itemRow : styles.itemRowAlt;
      const amountStyle =
        typeof amount === 'number' ? { ...rowStyle, ...styles.currency } : rowStyle;
      data.push([
        cell(item, rowStyle),
        cell(qty, { ...rowStyle, alignment: { horizontal: 'center', vertical: 'middle' } }),
        cell(amount, amountStyle),
      ]);
      rowIndex++;
    };

    // Member License
    addItemRow(
      `Member License @ ${formatCurrency(summary.discountedPricePerMember)}/user/month`,
      `${values.numberOfMembers} users`,
      summary.totalMemberCostYearly,
    );

    // Platform Fee
    if (summary.platformFeeYearly > 0) {
      addItemRow(
        `Platform Fee @ ${formatCurrency(summary.platformFeeMonthly)}/month (${summary.hoursIncluded} hrs included)`,
        `${values.subscriptionDuration} months`,
        summary.platformFeeYearly,
      );
    }

    // Additional Users
    if (values.numberOfInternalContractors > 0) {
      addItemRow(
        'Internal Contractors',
        `${values.numberOfInternalContractors} users`,
        summary.internalContractorsTotal,
      );
    }
    if (values.numberOfExternalContractors > 0) {
      addItemRow(
        'External Contractors',
        `${values.numberOfExternalContractors} users`,
        summary.externalContractorsTotal,
      );
    }
    if (values.numberOfGuests > 0) {
      addItemRow('Guests', `${values.numberOfGuests} users`, summary.guestsTotal);
    }

    // Server Upgrades
    if (summary.isolatedDatabase) {
      addItemRow('Isolated Database', '—', summary.isolatedDatabaseFeeYearly);
    }
    if (isDedicatedTenant(summary.tenantType) && summary.tenantFee > 0) {
      const tenantLabel =
        summary.tenantType === TenantType.SINGLE
          ? 'Single Tenant Environment'
          : 'Enterprise Tenant';
      addItemRow(tenantLabel, '—', summary.tenantFee);
    }
    if (summary.securityPackageSso) {
      addItemRow('SSO Security Package', `${values.numberOfMembers} users`, summary.ssoTotalYearly);
    }
    if (summary.govCloud) {
      addItemRow('Gov Cloud', '—', 'Included');
    }

    // Add-ons
    if (summary.aiForecasting) {
      addItemRow('AI Forecasting', '—', summary.aiForecastingFeeAfterDiscount);
    }
    if (summary.nonPeopleResources) {
      addItemRow('Non-People Resources', '—', summary.nonPeopleResourcesFeeAfterDiscount);
    }
    if (summary.profileBuilding) {
      addItemRow('Profile Building', '—', summary.profileBuildingFeeAfterDiscount);
    }
    if (summary.skillsTagging) {
      addItemRow('Skills Tagging', '—', summary.skillsTaggingFeeAfterDiscount);
    }
    if (summary.solutionsSupport) {
      addItemRow('Solutions Support', '—', summary.solutionsSupportFee);
    }
    if (summary.enableWhiteLabel) {
      addItemRow('White-Label Branding', '—', summary.whiteLabelAdditionalCost);
    }

    // Recurring Subtotal
    data.push([
      cell('RECURRING SUBTOTAL', styles.subtotal),
      cell('', styles.subtotal),
      cell(summary.recurringFeeSubtotal, { ...styles.subtotal, ...styles.currency }),
    ]);

    // Section: ONE-TIME FEES
    rowIndex = 0; // Reset alternating
    data.push([
      cell('ONE-TIME FEES', styles.sectionHeader),
      cell('', styles.sectionHeader),
      cell('', styles.sectionHeader),
    ]);

    // Onboarding
    const onboardingLabel =
      values.discountOnOnboardingFee > 0
        ? `Onboarding Fee (${values.discountOnOnboardingFee}% discount applied)`
        : 'Onboarding Fee';
    addItemRow(onboardingLabel, '—', summary.onboardingFeeAfterDiscount);

    // Direct Database
    if (summary.directDatabase) {
      addItemRow('Direct Database Integration', '—', summary.directDatabaseFee);
    }

    // One-Time Subtotal
    data.push([
      cell('ONE-TIME SUBTOTAL', styles.subtotal),
      cell('', styles.subtotal),
      cell(summary.oneTimeFeeSubtotal, { ...styles.subtotal, ...styles.currency }),
    ]);

    // Empty row before total
    data.push(['', '', '']);

    // Grand Total
    data.push([
      cell('TOTAL', styles.total),
      cell('', styles.total),
      cell(summary.totalCost, {
        ...styles.total,
        ...styles.currency,
        font: { ...styles.total.font },
      }),
    ]);

    // Empty rows
    data.push(['', '', '']);
    data.push(['', '', '']);

    // Footer
    data.push([
      cell('Terms & Conditions:', {
        ...styles.footer,
        font: { ...styles.footer.font, bold: true },
      }),
      '',
      '',
    ]);
    data.push([
      cell('• This quote is valid for 30 days from the date of issue.', styles.footer),
      '',
      '',
    ]);
    data.push([cell('• Prices shown are in USD.', styles.footer), '', '']);
    data.push([
      cell('• Contact your Mosaic account representative with questions.', styles.footer),
      '',
      '',
    ]);

    // Create the worksheet
    const sheet = workbook.addWorksheet('Quote');

    // Set column widths. exceljs stores `width` as is, while SheetJS stored a `wch`
    // character count plus 5px of cell padding (at its 6px max digit width), so
    // convert the same way to keep the columns as wide as the old export's.
    const charWidth = (chars: number) => Math.round(((chars * 6 + 5) / 6) * 256) / 256;
    sheet.columns = [
      { width: charWidth(55) }, // Item column
      { width: charWidth(18) }, // Quantity column
      { width: charWidth(18) }, // Amount column
    ];

    // Row height in points
    const rowHeight = (row: CellValue[], i: number) => {
      if (i === 0) return 32; // Title
      if (i === 2) return 24; // Subtitle
      // Check if it's a section header or total row
      const firstCell = row[0];
      if (typeof firstCell === 'object') {
        const { fill } = firstCell.s;
        const fillArgb = fill?.type === 'pattern' ? fill.fgColor?.argb : undefined;
        if (fillArgb === 'FF3B5998') return 24; // Section header
        if (fillArgb === 'FF1E3A5F' && i > 10) return 28; // Total row
      }
      return 20;
    };

    data.forEach((row, i) => {
      const sheetRow = sheet.getRow(i + 1);
      sheetRow.height = rowHeight(row, i);
      row.forEach((value, col) => {
        if (value === '') return; // Leave spacer cells blank
        const sheetCell = sheetRow.getCell(col + 1);
        if (typeof value === 'object') {
          sheetCell.value = value.v;
          sheetCell.style = value.s;
        } else {
          sheetCell.value = value;
        }
      });
    });

    // Write and save
    const excelBuffer = await workbook.xlsx.writeBuffer();

    const excelBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const fileName = values.companyName
      ? `Mosaic_Quote_${values.companyName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`
      : `Mosaic_Quote_${new Date().toISOString().split('T')[0]}`;

    saveAs(excelBlob, `${fileName}.xlsx`);
  }, [values, summary]);

  // Copy quote data to clipboard
  const copyQuoteToClipboard = useCallback(async () => {
    if (!summary) return;

    const quoteText = `
Pricing Quote - ${values.companyName || 'N/A'}
===============================

SUBSCRIPTION DETAILS
- Duration: ${values.subscriptionDuration} months
- Members: ${values.numberOfMembers}
- Plan: ${getPlanTypeLabel(values.planType)}

MEMBER PRICING
- Original Price: ${formatCurrency(summary.originalPricePerMember)}/member/month
- Discounted Price: ${formatCurrency(summary.discountedPricePerMember)}/member/month
- Total Member Cost: ${formatCurrency(summary.totalMemberCostYearly)}

PLATFORM FEE
- Platform Fee: ${formatCurrency(summary.platformFeeYearly)}

ONE-TIME FEES
- Onboarding Fee: ${formatCurrency(summary.onboardingFeeAfterDiscount)}
- One-time Subtotal: ${formatCurrency(summary.oneTimeFeeSubtotal)}

RECURRING FEES
- Recurring Subtotal: ${formatCurrency(summary.recurringFeeSubtotal)}

TOTAL: ${formatCurrency(summary.totalCost)}
    `.trim();

    try {
      await navigator.clipboard.writeText(quoteText);
    } catch {
      console.error('Failed to copy quote to clipboard');
    }
  }, [values, summary]);

  return {
    exportToPDF,
    exportToExcel,
    copyQuoteToClipboard,
  };
};

export default usePricingExport;
